/**
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/dashboard/stat-cards.tsx — one of that
 * file's `MetricCard` + recharts `BarChart` cards, with its hard-coded `eComDashboardStatData` row
 * turned into props. The bars are the API's own zero-filled daily buckets; the headline is the API's
 * own `totals` figure (constitution I — nothing here sums the bars to get it). The template's
 * "+32.40% Increased last month" footer is replaced by the period the figure covers: no endpoint
 * serves a previous-period comparison, and deriving one here would be client-side arithmetic.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { MetricCard, WidgetCard, cn } from "@steelyard/theme";
import { Text } from "rizzui";
import { problemMessage } from "./tone";
import { STEEL, DEEMPHASIS, bucketDay } from "./palette";
import { money } from "../../lib/services/ordering";

const COUNT = new Intl.NumberFormat("en-IN");

/** Display only. A value that will not parse shows as "—", never as a made-up 0. */
export function formatCount(value: number | string): string {
  const parsed = money(value);
  return parsed === null ? "—" : COUNT.format(parsed);
}

/** "1 invoice", "5 invoices" — the count is the API's; only the noun agrees with it. */
export function countOf(value: number | string, noun: string): string {
  return `${formatCount(value)} ${noun}${money(value) === 1 ? "" : "s"}`;
}

export type TrendPoint = { periodStart: string; value: number | null };

/** The sparkline's hover label: the bucket's date and the API's own figure for it, nothing derived. */
function SparkTip({ active, payload, format }: { active?: boolean; payload?: { payload: TrendPoint }[]; format: (value: number) => string }) {
  const point = active ? payload?.[0]?.payload : undefined;
  if (!point) return null;
  return (
    <div className="rounded-md border border-muted bg-gray-0 px-2.5 py-1.5 text-xs shadow-md dark:bg-gray-100">
      <span className="text-gray-500">{bucketDay(point.periodStart)}</span>{" "}
      <span className="font-medium text-gray-900">{point.value === null ? "—" : format(point.value)}</span>
    </div>
  );
}

export function TrendStatCard({
  title,
  metric,
  icon,
  points,
  format,
  period,
  footer,
  link,
}: {
  title: string;
  metric: ReactNode;
  icon: ReactNode;
  /** The API's zero-filled buckets, oldest first; the last one is the current day. */
  points: TrendPoint[];
  /** How a single bucket's value reads in the tooltip. */
  format: (value: number) => string;
  /** The window the figure covers, as a pill in the corner ("Last 30 days"). */
  period: string;
  /** What the figure counts — its definition, not the period again. */
  footer: string;
  link?: { to: string; label: string };
}) {
  return (
    <MetricCard
      title={title}
      metric={metric}
      titleClassName="pe-24 text-sm font-medium text-gray-600"
      metricClassName="mt-1 text-[28px] leading-9 tracking-tight"
      icon={icon}
      iconClassName="shrink-0 rounded-xl bg-primary/10 text-primary [&>svg]:size-6"
      className="@container relative h-full transition-shadow duration-200 hover:shadow-md [&>div:first-child]:items-start"
    >
      <span className="absolute end-4 top-4 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">{period}</span>
      {/* Full card width, so thirty days read as thirty bars rather than a smudge. Past days in the
          de-emphasis grey, the current day in steel; a hairline baseline so a zero day still reads
          as a stated zero. */}
      <div className="mt-5 h-14 w-full border-b border-gray-200">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={points} barCategoryGap={2} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
            <Tooltip cursor={{ fill: "rgba(42, 120, 214, 0.08)" }} content={<SparkTip format={format} />} isAnimationActive={false} />
            <Bar dataKey="value" radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {points.map((point, index) => (
                <Cell
                  key={point.periodStart}
                  fill={index === points.length - 1 ? STEEL : DEEMPHASIS}
                  // The theme's own grey token flips with dark mode; a CSS fill beats the attribute.
                  className={index === points.length - 1 ? undefined : "fill-gray-200"}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Text className="mt-3 flex items-center justify-between gap-2 text-xs leading-relaxed text-gray-500">
        <span>{footer}</span>
        {link ? (
          <Link to={link.to} className="text-primary font-medium hover:underline">
            {link.label}
          </Link>
        ) : null}
      </Text>
    </MetricCard>
  );
}

/**
 * The empty arm of every `WidgetCard` tile here: one muted icon and a line of text, sized to sit
 * inside a card rather than the full-page `EmptyState` illustration — four of those on a quiet day
 * turned the dashboard into a wall of dashed boxes. Still never styled as a failure (FR-028).
 */
export function EmptyTile({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="flex h-full min-h-[140px] flex-col items-center justify-center gap-1 py-6 text-center">
      <span aria-hidden className="mb-1 text-gray-300 [&>svg]:size-9">
        {icon}
      </span>
      <Text className="font-medium text-gray-900">{title}</Text>
      <Text className="max-w-xs text-sm text-gray-500">{children}</Text>
    </div>
  );
}

/**
 * The failed-read arm of every `WidgetCard` tile here (chart, donut, ranked list): the word
 * "Unreadable" first, then the caught problem's own detail (FR-002, FR-023). Without the word, a
 * bare detail like "Bad Gateway" does not say that the figures are missing, not zero.
 */
export function UnreadableWidgetCard({ title, error, fallback }: { title: string; error: unknown; fallback: string }) {
  return (
    <WidgetCard title={title}>
      <Text className="pt-4 font-lexend text-lg font-semibold text-red-dark">Unreadable</Text>
      <Text className="mt-1 text-sm text-gray-500">{problemMessage(error, fallback)}</Text>
    </WidgetCard>
  );
}

/** The same card's failed-read arm: says so in words, never a `0` (FR-002, SC-009). */
export function UnreadableStatCard({ title, icon, error, fallback }: { title: string; icon: ReactNode; error: unknown; fallback: string }) {
  return (
    <MetricCard
      title={title}
      metric={<span className="text-red-dark">Unreadable</span>}
      titleClassName="text-sm font-medium text-gray-600"
      metricClassName="mt-1 text-xl"
      icon={icon}
      iconClassName={cn("rounded-xl bg-red-lighter [&>svg]:size-6", "text-red-dark")}
      className="@container h-full [&>div:first-child]:items-start"
    >
      <Text className="mt-5 border-t border-muted pt-3 text-xs leading-relaxed text-gray-500">{problemMessage(error, fallback)}</Text>
    </MetricCard>
  );
}
